import { useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Icon,
  Test,
  Dialog,
  isMobile,
  DialogType,
  FlexLayout,
  useNavigate,
  ButtonColors,
  BurgerWithRef,
  StandardButton,
  ButtonStyleTypes,
} from '@gv/triage-components';

import { AppRoutes } from 'config';

import { GVIcon } from '../icons';

import * as Styles from './styles';

const { testIds } = Test;

/**
 * The Header component provides a navigation bar for both desktop and mobile views.
 * It includes a logo that navigates to the home page, and responsive login/signup buttons
 * that adapt their layout and functionality based on the device type.
 *
 * In mobile view, the buttons are presented in a modal dialog triggered by a hamburger menu icon,
 * whereas in desktop view, they are directly displayed in the header.
 *
 */
export const Header = () => {
  const mobile = isMobile();
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthPath = useMemo(() => {
    const pathes = Object.values(AppRoutes.Auth);
    return pathes.includes(location.pathname);
  }, [location.pathname]);

  /**
   * Generates a component containing buttons for navigating to the login and signup pages.
   * This component is responsive, adjusting its layout and spacing based on the 'mobile' variable.
   *
   * @param close - An optional callback function that is called when a button is clicked.
   * @returns A component containing two buttons ('Log In' and 'Sign Up').
   */
  const getButtons = useCallback(
    (close?: () => void) => {
      const onButtonClick = (to: string) => {
        navigate(to);
        close?.();
      };
      return (
        <FlexLayout
          fullWidth={mobile}
          alignItems="center"
          gap={mobile ? 16 : 24}
          flexDirection={mobile ? 'column' : 'row'}
        >
          <p className="semibold">For veterinarians:</p>
          <StandardButton
            text="Log In"
            fullWidth={mobile}
            testId={testIds.buttons.logIn}
            styleType={ButtonStyleTypes.Ghost}
            onClick={() => onButtonClick(AppRoutes.Auth.Login)}
          />
          <StandardButton
            text="Sign Up"
            fullWidth={mobile}
            testId={testIds.buttons.signUp}
            onClick={() => onButtonClick(AppRoutes.Auth.SignUp)}
          />
        </FlexLayout>
      );
    },
    [mobile]
  );

  return (
    <Styles.Container>
      <StandardButton
        padding="0"
        transparent
        nativeColorIcon
        to={AppRoutes.Home}
        icon={<GVIcon.GetVetLogo />}
        testId={testIds.buttons.logo}
      />

      {!isAuthPath && mobile && (
        <Dialog trigger={<BurgerWithRef />}>
          {(close, _, Buttons) => (
            <FlexLayout
              gap={16}
              fullWidth
              flexDirection="column"
              data-testid={testIds.popup.dialog}
            >
              <FlexLayout fullWidth justifyContent="space-between">
                <h2>Menu</h2>
                <StandardButton
                  transparent
                  padding="0"
                  onClick={close}
                  icon={<Icon.Close />}
                  testId={testIds.buttons.close}
                  colorType={ButtonColors.BlackWhite}
                />
              </FlexLayout>
              <Styles.Divider />
              <Buttons type={DialogType.Plain}>{getButtons(close)}</Buttons>
            </FlexLayout>
          )}
        </Dialog>
      )}

      {!isAuthPath && !mobile && getButtons()}
    </Styles.Container>
  );
};
